import React, { useState } from "react";
import {
  ActivityIndicator,
  SafeAreaView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, useLocalSearchParams } from "expo-router";
import CommonServices from "@/services/commonServices";
import useUserStore from "@/store/user";
import LottieConfetti from "@/components/LottieConfetti";

const CancelOfferScreen = () => {
  const { getUserProfile } = useUserStore();
  const params = useLocalSearchParams<{
    reason: string;
    feedback: string;
    offerData: string;
  }>();

  const [isApplyingOffer, setIsApplyingOffer] = useState(false);

  const handleApplyDiscount = async () => {
    try {
      setIsApplyingOffer(true);
      const res = await CommonServices.claimCancellationOffer();

      if (res.success) {
        await getUserProfile();
        router.dismissAll();
        router.navigate("/(tabs)/account");
      } else {
        console.error("Apply offer failed");
      }
    } catch (error) {
      console.error("Apply offer error:", error);
    } finally {
      setIsApplyingOffer(false);
    }
  };

  const handleCancelAnyway = () => {
    // Navigate to claims screen (next step in cancel flow)
    router.push({
      pathname: "/account/cancel/claims",
      params: {
        reason: params.reason || "",
        feedback: params.feedback || "",
      },
    });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>A special discount{"\n"}for you!</Text>
        </View>

        <View style={styles.discountGraphic}>
          <Text style={styles.discountNumber}>50</Text>
          <Text style={styles.discountPercent}>%</Text>
        </View>

        <View style={styles.description}>
          <Text style={styles.descriptionText}>
            We understand your experience with Sparrow wasn&apos;t up to par.
          </Text>
          <Text style={styles.descriptionText}>
            {"\n"}As a thank you for trying Sparrow, we&apos;d like to{" "}
            <Text style={styles.descriptionBold}>offer 50% off</Text> your
            subscription for the next year if you choose to stay.
          </Text>
        </View>
      </View>

      <View style={styles.bottomSection}>
        <ActionButton
          title={isApplyingOffer ? "Applying..." : "Get my discount"}
          type="primary"
          onPress={handleApplyDiscount}
          disabled={isApplyingOffer}
          style={styles.primaryButton}
          icon={
            isApplyingOffer ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : undefined
          }
        />

        <TouchableOpacity
          onPress={handleCancelAnyway}
          disabled={isApplyingOffer}
          style={styles.cancelAnywayButton}
        >
          <Text style={styles.cancelAnywayText}>Cancel anyway</Text>
        </TouchableOpacity>
      </View>
      <LottieConfetti active={true} />
    </SafeAreaView>
  );
};

export default CancelOfferScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 60,
    alignItems: "center",
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 37,
  },
  discountGraphic: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    height: 180,
    marginBottom: 24,
  },
  discountNumber: {
    fontSize: 150,
    fontFamily: FontFamilies.Inter.Bold,
    color: "#0067BB",
    lineHeight: 150,
    textShadowColor: "rgba(13, 45, 71, 0.3)",
    textShadowOffset: { width: -1, height: 5 },
    textShadowRadius: 5,
  },
  discountPercent: {
    fontSize: 80,
    fontFamily: FontFamilies.Inter.ExtraBold,
    color: "#0067BB",
    lineHeight: 80,
    marginTop: 0,
  },
  description: {
    paddingHorizontal: 16,
  },
  descriptionText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    textAlign: "center",
    lineHeight: 20,
  },
  descriptionBold: {
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 34,
    paddingTop: 12,
    backgroundColor: Colors.background,
    gap: 12,
  },
  primaryButton: {
    width: "100%",
  },
  cancelAnywayButton: {
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelAnywayText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: Colors.primary,
    textAlign: "center",
    lineHeight: 23,
  },
});
